import { routes } from "router/routes"
import { DEACTIVATE_DELETE_MODES } from "./constants"

/**
 * Generates a trip/experience link based on available identifiers
 * @param {Object} trip - Trip/Experience object
 * @param {number|string} trip.trip_id - Trip ID
 * @param {string} trip.status - Trip status (published, draft, unpublished, deactivated)
 * @returns {string} - The generated link
 */
export const generateTripLink = (trip) => {
  const { trip_id, status } = trip || {}

  if (trip_id) {
    // Same targets as ExperienceCard's Edit; the wizard sets ?type= from the loaded experience
    if (status === "draft") return `#${routes.proNewExperienceV2Path({ id: trip_id })}`
    const route = ["deactivated", "unpublished"].includes(status) ? "deactivated" : "published"
    return `#${routes.proEditExperienceV2Path({ id: trip_id }, { route })}`
  }

  return "#"
}

/**
 * Generates a booking details link based on booking UUID
 * @param {Object} booking - Booking object
 * @param {string} booking.booking_uuid - Booking UUID
 * @returns {string} - The generated link
 */
export const generateBookingLink = (booking) => {
  const { booking_uuid } = booking || {}

  if (booking_uuid) {
    return `#${routes.guideBookingDetailsPath({ id: booking_uuid })}`
  }

  return "#"
}

/**
 * Determines which deactivate/delete modal mode to use based on resource data
 * Works for both equipment and crew members
 * @param {Object} details - Detailed resource data from API
 * @param {string} details.mode - Direct mode from API (preferred)
 * @param {string} details.status - API status
 * @param {Array} details.assigned_trips - Array of assigned trips
 * @param {Array} details.active_bookings - Array of active bookings
 * @returns {string} One of DEACTIVATE_DELETE_MODES
 */
export const determineDeactivateMode = (details) => {
  const {
    mode,
    status,
    assigned_trips = [],
    active_bookings = []
  } = details || {}

  // Priority 1: If API provides mode field directly, use it
  if (mode) {
    return mode
  }

  // Priority 2: If API provides status field, map it to mode
  if (status) {
    const statusModeMap = {
      no_experiences_assigned: DEACTIVATE_DELETE_MODES.NO_EXPERIENCES,
      no_active_bookings: DEACTIVATE_DELETE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING,
      fully_substitutable: DEACTIVATE_DELETE_MODES.FULLY_SUBSTITUTABLE,
      partially_substitutable: DEACTIVATE_DELETE_MODES.PARTIALLY_SUBSTITUTABLE
    }
    return statusModeMap[status] || DEACTIVATE_DELETE_MODES.NO_EXPERIENCES
  }

  // Fallback: Calculate mode from data if status not provided
  const assignedTripsCount = assigned_trips.length
  const activeBookingsCount = active_bookings.length

  if (assignedTripsCount === 0 && activeBookingsCount === 0) {
    return DEACTIVATE_DELETE_MODES.NO_EXPERIENCES
  }

  if (assignedTripsCount > 0 && activeBookingsCount === 0) {
    return DEACTIVATE_DELETE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING
  }

  if (activeBookingsCount > 0) {
    const allBookingsSubstitutable = active_bookings.every(
      (booking) => booking.can_substitute === true
    )

    if (allBookingsSubstitutable) {
      return DEACTIVATE_DELETE_MODES.FULLY_SUBSTITUTABLE
    } else {
      return DEACTIVATE_DELETE_MODES.PARTIALLY_SUBSTITUTABLE
    }
  }

  return DEACTIVATE_DELETE_MODES.NO_EXPERIENCES
}
